import React from "react";
import { createPortal } from "react-dom";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react-dom";
import { CalendarDays } from "lucide-react";
import { applyDateInputMask, selectDateInputDaySegment } from "./DataManagerOrdersPrimitives.jsx";
import { DispatchDayCalendarPopover } from "./resultsPageChrome/DispatchDayCalendarPopover.jsx";
import useDispatchDayCalendarContext from "../hooks/useDispatchDayCalendarContext.js";
import usePopoverDismiss from "../hooks/usePopoverDismiss.js";
import { focusCalendarDay } from "../utils/dispatchDayCalendarUtils.js";
import {
  parseDisplayDateInputValue,
  toDisplayDateInputValue,
} from "../hooks/useDataManagerOrdersUtils.js";

function resolveFloatingShiftPadding() {
  if (typeof window === "undefined") {
    return {
      bottom: 12,
      left: 12,
      right: 12,
      top: 12,
    };
  }

  const shellElement = document.querySelector("[data-app-shell='true']");
  const shellStyles = shellElement instanceof HTMLElement
    ? window.getComputedStyle(shellElement)
    : null;
  const sidebarWidthValue = shellStyles?.getPropertyValue("--shell-sidebar-width")?.trim() || "";
  const sidebarWidth = Number.parseFloat(sidebarWidthValue);
  const usesDesktopSidebar = window.matchMedia("(min-width: 1281px)").matches;
  const resolvedSidebarInset = usesDesktopSidebar && Number.isFinite(sidebarWidth)
    ? sidebarWidth + 16
    : 16;

  return {
    bottom: 12,
    left: resolvedSidebarInset,
    right: 12,
    top: 12,
  };
}

export default function DataManagerDispatchDatePickerInput({
  buttonClassName,
  inputClassName,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  popoverPositioning = "anchored",
  value = "",
  ...inputProps
}) {
  const containerRef = React.useRef(null);
  const calendarButtonRef = React.useRef(null);
  const dayButtonRefs = React.useRef([]);
  const textInputRef = React.useRef(null);
  const skipBlurCommitRef = React.useRef(false);
  const popoverId = React.useId();
  const titleId = React.useId();
  const [isFocused, setIsFocused] = React.useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);
  const [editText, setEditText] = React.useState(() => toDisplayDateInputValue(value));
  const isPopoverOpen = isCalendarOpen && !inputProps.disabled;
  const usesFloatingPopover = popoverPositioning === "floating";
  const {
    activeMonthKey,
    controls,
    errorMessage,
    handleSelectMonthKey,
    handleToggleArchive,
    isLoading,
    loadingMessage,
    showArchive,
  } = useDispatchDayCalendarContext({
    activeDate: value,
    isOpen: isPopoverOpen,
  });
  const shouldFloatPopover = isPopoverOpen && usesFloatingPopover;
  const { floatingStyles, isPositioned, refs } = useFloating({
    middleware: [
      offset(8),
      flip({ padding: resolveFloatingShiftPadding() }),
      shift({ padding: resolveFloatingShiftPadding() }),
    ],
    open: shouldFloatPopover,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
  });
  const [isPopoverVisible, setIsPopoverVisible] = React.useState(false);

  const setContainerRef = React.useCallback((node) => {
    containerRef.current = node;
    if (usesFloatingPopover) {
      refs.setReference(node);
    }
  }, [refs, usesFloatingPopover]);

  React.useLayoutEffect(() => {
    if (!shouldFloatPopover || !isPositioned) {
      setIsPopoverVisible(false);
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      setIsPopoverVisible(true);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isPositioned, shouldFloatPopover]);

  React.useEffect(() => {
    if (!isFocused) {
      setEditText(toDisplayDateInputValue(value));
    }
  }, [isFocused, value]);

  const displayText = isFocused ? editText : toDisplayDateInputValue(value);

  const emitIsoValue = React.useCallback((nextIsoValue) => {
    onChange?.({
      target: {
        value: nextIsoValue,
      },
    });
  }, [onChange]);

  const applyIsoValue = React.useCallback((nextIsoValue) => {
    skipBlurCommitRef.current = true;
    setIsFocused(false);
    setEditText(nextIsoValue ? toDisplayDateInputValue(nextIsoValue) : "");
    emitIsoValue(nextIsoValue);
    textInputRef.current?.blur();
  }, [emitIsoValue]);

  const handleOpenPicker = React.useCallback(() => {
    setIsCalendarOpen((currentValue) => !currentValue);
  }, []);

  const handleCloseCalendar = React.useCallback(({ restoreFocus = false } = {}) => {
    setIsCalendarOpen(false);

    if (!restoreFocus || typeof window === "undefined") {
      return;
    }

    window.requestAnimationFrame(() => {
      calendarButtonRef.current?.focus({ preventScroll: true });
    });
  }, []);

  const handleSelectCalendarDate = React.useCallback((nextIsoValue) => {
    applyIsoValue(nextIsoValue);
    setIsCalendarOpen(false);
  }, [applyIsoValue]);

  usePopoverDismiss({
    anchorElement: containerRef.current,
    floatingRef: usesFloatingPopover ? refs.floating : null,
    isOpen: isPopoverOpen,
    onClose() {
      handleCloseCalendar();
    },
  });

  React.useEffect(() => {
    if (!isPopoverOpen || (usesFloatingPopover && !isPositioned) || typeof window === "undefined" || isLoading || errorMessage) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      const activeDayIndex = dayButtonRefs.current.findIndex(
        (element) => element?.getAttribute?.("aria-current") === "date",
      );
      const targetDayIndex = activeDayIndex >= 0
        ? activeDayIndex
        : dayButtonRefs.current.findIndex(Boolean);

      if (targetDayIndex < 0) {
        return;
      }

      const targetDay = dayButtonRefs.current[targetDayIndex];
      focusCalendarDay(targetDay);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [activeMonthKey, errorMessage, isLoading, isPopoverOpen, isPositioned, usesFloatingPopover, value]);

  const calendarPopover = (
    <DispatchDayCalendarPopover
      activeDate={value}
      activeMonthKey={activeMonthKey}
      controls={controls}
      dayButtonRefs={dayButtonRefs}
      errorMessage={errorMessage}
      floatingStyles={usesFloatingPopover ? floatingStyles : null}
      isOpen={isPopoverOpen}
      isPositioned={usesFloatingPopover ? isPopoverVisible : true}
      loadingMessage={loadingMessage}
      onClose={handleCloseCalendar}
      onSelectDate={handleSelectCalendarDate}
      onSelectMonthKey={handleSelectMonthKey}
      onToggleArchive={handleToggleArchive}
      popoverId={popoverId}
      positioning={usesFloatingPopover ? "floating" : "anchored"}
      setFloatingRef={usesFloatingPopover ? refs.setFloating : null}
      showArchive={showArchive}
      titleId={titleId}
    />
  );

  return (
    <div ref={setContainerRef} className="relative min-w-0">
      <input
        {...inputProps}
        ref={textInputRef}
        className={inputClassName}
        data-date-segment-input="day-first"
        inputMode="numeric"
        onBlur={(event) => {
          setIsFocused(false);
          if (skipBlurCommitRef.current) {
            skipBlurCommitRef.current = false;
            onBlur?.(event);
            return;
          }
          const nextIsoValue = parseDisplayDateInputValue(editText);
          if (nextIsoValue) {
            emitIsoValue(nextIsoValue);
            setEditText(toDisplayDateInputValue(nextIsoValue));
          } else if (!editText.trim()) {
            emitIsoValue("");
            setEditText("");
          } else {
            setEditText(toDisplayDateInputValue(value));
          }
          onBlur?.(event);
        }}
        onChange={(event) => {
          const nextEditText = applyDateInputMask(event.target);
          setEditText(nextEditText);
          const nextIsoValue = parseDisplayDateInputValue(nextEditText);
          if (/^\d{2}-\d{2}-\d{4}$/.test(nextEditText) && nextIsoValue) {
            emitIsoValue(nextIsoValue);
          } else if (!nextEditText.trim()) {
            emitIsoValue("");
          }
        }}
        onFocus={(event) => {
          const nextEditText = toDisplayDateInputValue(value);
          setEditText(nextEditText);
          setIsFocused(true);
          setIsCalendarOpen(false);
          selectDateInputDaySegment(event.target);
          onFocus?.(event);
        }}
        onKeyDown={onKeyDown}
        type="text"
        value={displayText}
      />
      <button
        ref={calendarButtonRef}
        aria-controls={popoverId}
        aria-expanded={isCalendarOpen}
        aria-haspopup="dialog"
        aria-label="Ouvrir le calendrier dispatch"
        className={[buttonClassName, "z-40"].filter(Boolean).join(" ")}
        disabled={inputProps.disabled}
        onClick={handleOpenPicker}
        onMouseDown={(event) => event.preventDefault()}
        tabIndex={-1}
        type="button"
      >
        <CalendarDays size={12} />
      </button>
      {isPopoverOpen
        ? (usesFloatingPopover && typeof document !== "undefined"
          ? createPortal(calendarPopover, getReactPortalRoot())
          : calendarPopover)
        : null}
    </div>
  );
}
